import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { Config } from '../config';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';
import Toast from '../components/Toast';

export default function ForgotPassword() {
  const labels = useLabels();
  const t = useT();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { state: { message: 'Password reset email sent! Please check your inbox.' } });
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.failedSendReset'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-eucnet-gradient flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <Toast
        open={success}
        setOpen={setSuccess}
        type="success"
        className="fixed top-6 right-6 z-50"
      >
        {t('auth.passwordResetEmailSent')}
      </Toast>

      <div className="max-w-md w-full space-y-8">
        <div>
          {/* EU CyberNet Logo */}
          <div className="flex justify-center overflow-hidden max-h-[140px] mb-0">
            <img
              src={labels.loginLogo || Config.loginLogo}
              alt="Logo"
              className="h-[196px] w-auto object-top"
            />
          </div>
          <h2 className="mt-2 text-center text-3xl font-bold text-white tracking-tight">
            {labels.platformTitle}
          </h2>
          <p className="mt-3 text-center text-2xl text-white/90 font-medium">
            {labels.platformSubtitle}
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-eucnet-navy mb-2">{t('auth.forgotPasswordTitle')}</h2>
            <p className="text-sm text-gray-600">
              {t('auth.forgotPasswordDesc')}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-eucnet-navy mb-2">
                {t('auth.emailAddress')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="block w-full px-4 py-3 border border-eucnet-grey-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-eucnet-sky focus:border-eucnet-sky transition-colors"
                placeholder={t('auth.enterEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-4 rounded-lg">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-700 text-sm bg-green-50 border border-green-200 p-4 rounded-lg">
                {t('auth.passwordResetSent')}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || success}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-base font-semibold text-white bg-gradient-to-r from-eucnet-primary to-eucnet-sky hover:from-eucnet-navy hover:to-eucnet-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-eucnet-sky disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
              >
                {loading ? t('auth.sending') : success ? t('auth.emailSent') : t('auth.sendResetLink')}
              </button>
            </div>

            <div className="text-center">
              <Link to="/login" className="text-sm font-medium text-eucnet-blue hover:text-eucnet-sky transition-colors">
                {t('auth.backToLogin')}
              </Link>
            </div>
          </form>
        </div>

        <p className="text-center text-sm text-white/80 font-medium">
          © 2026 {labels.brandName}. {t('common.allRightsReserved')}
        </p>
      </div>
    </div>
  );
}
